// =============================================================================
// PWA Status — Offline banner + Update notification
// =============================================================================
import { useState, useEffect } from "react";
import { WifiOff, RefreshCw, X } from "lucide-react";

export function PWAStatus() {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    const goOnline = () => setIsOffline(false);
    const goOffline = () => setIsOffline(true);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);

    // Listen for SW update message
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener("message", (event) => {
        if (event.data?.type === "SW_UPDATED") {
          setUpdateAvailable(true);
        }
      });
    }

    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  // Auto-bascule sur la nouvelle version dès que le nouveau SW prend le contrôle
  // (`controllerchange`). Le SW fait skipWaiting()+clients.claim() tout seul → cet
  // évènement arrive en quelques secondes après un deploy, SANS clic ni nav privée.
  // Garde-fous : (1) pas de reload au tout 1er claim (1re visite, pas une MAJ) ;
  // (2) un seul reload ; (3) si une SAISIE est en cours, on NE reload PAS brutalement
  // (perte de données) → bandeau manuel + reload différé dès l'arrêt de l'édition.
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    const isEditing = () => {
      const el = document.activeElement;
      if (!el) return false;
      const tag = el.tagName;
      return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable === true;
    };

    let controlled = !!navigator.serviceWorker.controller;  // déjà contrôlé = visite avec SW actif
    let reloaded = false;
    let blurTimer = null;

    const reloadOnce = () => {
      if (reloaded) return;
      reloaded = true;
      window.location.reload();
    };
    const scheduleSafeReload = () => {
      clearTimeout(blurTimer);
      // 2 s après le dernier blur ; un re-focus (tab entre champs) annule.
      blurTimer = setTimeout(() => { if (!isEditing()) reloadOnce(); }, 2000);
    };
    const cancelTimer = () => clearTimeout(blurTimer);

    const onControllerChange = () => {
      if (!controlled) { controlled = true; return; }  // 1er claim (install initiale) → pas de reload
      if (!isEditing()) { reloadOnce(); return; }       // cas nominal : bascule immédiate
      // Saisie en cours → différer pour ne pas perdre la frappe.
      setUpdateAvailable(true);
      window.addEventListener("focusout", scheduleSafeReload, true);
      window.addEventListener("focusin", cancelTimer, true);
    };

    navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);
    return () => {
      navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
      window.removeEventListener("focusout", scheduleSafeReload, true);
      window.removeEventListener("focusin", cancelTimer, true);
      clearTimeout(blurTimer);
    };
  }, []);

  const handleUpdate = () => {
    if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: "SKIP_WAITING" });
    }
    window.location.reload();
  };

  if (!isOffline && !updateAvailable) return null;

  return (
    <>
      {isOffline && (
        <div
          className="fixed top-0 left-0 right-0 z-[9999] flex items-center justify-center gap-2 py-2 px-4 text-sm font-medium"
          style={{ background: "var(--oh-stat-red-text)", color: "#fff" }}
          data-testid="pwa-offline-banner"
        >
          <WifiOff className="w-4 h-4" />
          <span>Hors connexion — certaines fonctions sont indisponibles</span>
        </div>
      )}
      {updateAvailable && (
        <div
          className="fixed bottom-4 right-4 z-[9999] flex items-center gap-3 rounded-lg px-4 py-3 shadow-lg border text-sm"
          style={{
            background: "var(--oh-bg-elevated, #1a1a2e)",
            borderColor: "var(--oh-stat-blue-text)",
            color: "var(--oh-text, #fff)",
          }}
          data-testid="pwa-update-banner"
        >
          <RefreshCw className="w-4 h-4 text-[var(--oh-accent-text)]" />
          <span>Nouvelle version disponible</span>
          <button
            onClick={handleUpdate}
            className="ml-2 px-3 py-1 rounded text-xs font-medium text-[var(--oh-text)]"
            style={{ background: "var(--oh-stat-blue-text)" }}
            data-testid="pwa-update-btn"
          >
            Mettre a jour
          </button>
          <button
            onClick={() => setUpdateAvailable(false)}
            className="ml-1 p-1 rounded hover:bg-white/10"
            data-testid="pwa-dismiss-btn"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}
    </>
  );
}
